import {Component, inject, signal, ViewEncapsulation, WritableSignal} from '@angular/core';
import {EsvgFiles, FeedbackService, MetabaseService} from "frontier";
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {filter, map} from 'rxjs/operators';
import {Observable, of, switchMap} from 'rxjs';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {NgForOf} from '@angular/common';
import {MatDialog} from '@angular/material/dialog';
import {
  DateRangePickerDialogComponent
} from 'frontier/browserkit/src/lib/dialogs/date-range-picker-dialog/date-range-picker-dialog.component';
import {WindmillExportsService} from '../../../api/generated';
import {DownloadService} from 'frontier/browserkit/src/lib/services/download.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

interface IDateRange {
  description: string;
  fromDate: Date;
  toDate: Date;
}

@Component({
  selector: 'app-metabase-dashboard',
  standalone: true,
  imports: [
    MatButton,
    MatIcon,
    MatMenu,
    MatMenuItem,
    NgForOf,
    MatMenuTrigger,
    MatProgressSpinner
  ],
  templateUrl: './metabase-dashboard.component.html',
  styleUrl: './metabase-dashboard.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class MetabaseDashboardComponent {
  private _metabaseService: MetabaseService = inject(MetabaseService);
  private _windmillExportService = inject(WindmillExportsService);
  private _downloadService = inject(DownloadService);
  private _sanitizer: DomSanitizer = inject(DomSanitizer);
  private _dialog = inject(MatDialog);
  private _feedbackService = inject(FeedbackService)

  protected readonly EsvgFiles = EsvgFiles;


  sanitizedUrl: WritableSignal<SafeResourceUrl> = signal<SafeResourceUrl>(null);
  error: WritableSignal<string> = signal<string>(null);
  loadingk5: WritableSignal<boolean> = signal<boolean>(false);

  currentDate = new Date()

  possibleK5Dates: IDateRange[] = [
    {
      description: 'Letztes Jahr',
      fromDate: new Date(Date.UTC(this.currentDate.getFullYear() - 1, 0, 1)),
      toDate: new Date(Date.UTC(this.currentDate.getFullYear() - 1, 11, 31))
    },
    {
      description: 'Aktuelles Jahr',
      fromDate: new Date(Date.UTC(this.currentDate.getFullYear(), 0, 1)),
      toDate: new Date(Date.UTC(this.currentDate.getFullYear(), 11, 31))
    },
    {description: 'Beliebiger Zeitraum', fromDate: null, toDate: null},
  ]

  constructor() {
    this._metabaseService.metabaseGetGetAuthenticateUrl()
      .pipe(map((res: string): SafeResourceUrl => {
          return this._sanitizer.bypassSecurityTrustResourceUrl(`${this._metabaseService.configuration.basePath}/${res}`)
        })
      ).subscribe((res: SafeResourceUrl) => {
      this.sanitizedUrl.set(res)
    })
  }

  exportK5(dateRange: IDateRange) {
    this.loadingk5.set(true);

    try {
      const datePickerObs = this.getDateRangeObservable(dateRange);

      datePickerObs.pipe(
        filter(result => result != null), // Handle dialog cancellation
        switchMap(({fromDate, toDate}: { fromDate: string, toDate: string }) => {
          console.log('Exporting K5 for date range', fromDate, toDate);
          return this._windmillExportService.windmillExportsGetK5Data({
            start_date: fromDate,
            end_date: toDate
          })
        })
      ).subscribe({
        next: (response) => {
          console.log('Export successful', response);
          this._downloadService.downloadFromBlobResponseWithFileName(response, "k5.xlsx")
        },
        error: (error) => {
          this._feedbackService.setError("Export fehlgeschlagen. Ein unbekannter Fehler ist aufgetreten.")
          console.error('Export failed', error);
        }, complete: () => this.loadingk5.set(false)
      });
    } catch (e) {
      this._feedbackService.setError("Export fehlgeschlagen. Ein unbekannter Fehler ist aufgetreten.")
      this.loadingk5.set(false);
      console.error('Error initializing export', e);
    }
  }

  private getDateRangeObservable(dateRange: IDateRange): Observable<{ fromDate: string, toDate: string } | null> {
    // If dates are provided, return them directly
    if (dateRange.fromDate && dateRange.toDate) {
      return of({
        fromDate: this.formatDateToISO(dateRange.fromDate),
        toDate: this.formatDateToISO(dateRange.toDate)
      });
    }

    // Open dialog for custom date range
    return this._dialog.open(DateRangePickerDialogComponent, {
      maxWidth: '35rem',
      data: {
        title: 'Zeitraum für K5-Export wählen',
        mode: 'range',
        fromLabel: 'Von',
        toLabel: 'Bis'
      }
    }).afterClosed().pipe(
      map(result => result ? {
        fromDate: result.fromDate,
        toDate: result.toDate
      } : null)
    );
  }

  private formatDateToISO(date: Date): string {
    return date.toISOString().substring(0, 10);
  }

}
